/**
 * Which node is under a point, resolved on the CPU.
 *
 * On the CPU rather than by reading back a picking buffer, because a readback is asynchronous: the
 * answer arrives a frame or more after the pointer event that asked for it, which on a fast drag
 * shows as a tooltip trailing the cursor. Projecting a few hundred centers through the same matrix
 * the shader uses costs less than the event handler that called it.
 *
 * The matrix comes from `toViewProjectionMatrix`, which is asserted equal to the software
 * projector - so this agrees with what was drawn rather than approximating it.
 */

import type { Size, ViewCamera } from '../../types';
import {
  applyViewProjection,
  clipToScreen,
  toViewProjectionMatrix,
} from '../../geometry/viewProjection';
import { NODE_FLOATS, type NetworkInstances } from './packNetwork';

export type FindNetworkNodeArgs = {
  instances: NetworkInstances;
  camera: ViewCamera;
  /** In CSS pixels, matching the pointer coordinates. */
  viewport: Size;
  /** Cursor position within the canvas, in CSS pixels. */
  point: { x: number; y: number };
  /**
   * Extra pixels added to every node's own radius.
   *
   * A node is drawn a few pixels across and a pointer is not a pixel, so hitting one exactly is
   * fiddly - the hit target has to be larger than the mark. Kept modest, because in a dense
   * cluster an over-generous target resolves to whichever node happens to be nearest the eye
   * rather than the one being aimed at.
   */
  tolerance: number;
};

/**
 * Below this, in pixels, a mark is not a target.
 *
 * `packNetworkInstances` writes an instance for every node, including the ones drawn as nothing -
 * which is how an expanded group says it is a region rather than a mark. Those project to well
 * under a pixel, but the tolerance was added to that, so every group centre was hit-testable
 * across the full target whatever it drew. The centre of a group is empty space by construction,
 * since the packing seats its children on a shell around it, so this produced a tooltip over
 * apparently blank canvas reading the group's name, an empty type and "0 references" - and worse,
 * the tie-break on depth let an invisible group shadow a real component projecting behind it.
 *
 * A third of a pixel rather than zero: a mark can legitimately be tiny on a dense estate, and the
 * thing being excluded is not small but absent.
 */
const SMALLEST_HITTABLE = 0.33;

export const findNetworkNodeAt = ({
  instances,
  camera,
  viewport,
  point,
  tolerance,
}: FindNetworkNodeArgs): number | null => {
  if (instances.nodeCount === 0) return null;
  if (viewport.width === 0 || viewport.height === 0) return null;

  const matrix = toViewProjectionMatrix(camera, viewport);
  /* The same scale the matrix bakes in, needed again here to turn a world radius into pixels. */
  const pixelsPerUnit =
    (Math.min(viewport.width, viewport.height) / 2) * camera.zoom;

  let closest: number | null = null;
  /* Tie-broken by clip w rather than by screen distance: a smaller w is nearer the eye under this
     projection, and between two overlapping nodes the one in front is the one that was drawn. */
  let nearest = Infinity;

  for (let index = 0; index < instances.nodeCount; index++) {
    const at = index * NODE_FLOATS;
    const clip = applyViewProjection(
      matrix,
      instances.nodes[at],
      instances.nodes[at + 1],
      instances.nodes[at + 2]
    );
    const { screenX, screenY } = clipToScreen(clip, viewport);

    const drawn = (instances.nodes[at + 3] * pixelsPerUnit) / clip.w;
    /* Skipped before the tolerance is added, which is the whole point: a mark drawn as nothing
       would otherwise carry the same target as one drawn as something. */
    if (drawn < SMALLEST_HITTABLE) continue;

    const radius = drawn + tolerance;
    const distance = Math.hypot(screenX - point.x, screenY - point.y);

    if (distance <= radius && clip.w < nearest) {
      nearest = clip.w;
      closest = index;
    }
  }

  return closest;
};
